import { useState } from 'react';
import { decidirDivergencia } from '../../api/aberturas.js';
import type { Abertura, DecisaoDaDivergencia, DivergenciaDecidida } from '../../api/aberturas.js';
import { mensagemDoErro } from '../../api/erros.js';
import { CampoAreaTexto } from '../../componentes/Campo.js';
import { DialogoConfirmacao } from '../../componentes/DialogoConfirmacao.js';
import { formatarQuantidade, quantidadeQueFaltava, somaDasEntregas } from './quantidade.js';
import './DialogosDaAbertura.css';

/** Teto do middleware para a justificativa (zod `.max(500)`). */
export const LIMITE_DO_TEXTO = 500;

/** A abertura em divergência, com a entrega retida garantida. */
export type AberturaEmDivergencia = Abertura & {
  readonly retida: NonNullable<Abertura['retida']>;
};

export function estaEmDivergencia(abertura: Abertura): abertura is AberturaEmDivergencia {
  return abertura.status === 'EmDivergencia' && abertura.retida !== null;
}

function exibir(texto: string | null): string {
  return texto === null ? '—' : formatarQuantidade(texto);
}

function saldoDe(abertura: AberturaEmDivergencia) {
  return {
    quantidade: abertura.quantidade,
    entregue: abertura.entregue,
    retida: abertura.retida.quantidade,
  };
}

/**
 * O rótulo do botão diz o número que vai valer: "Manter 10", "Atualizar para
 * 11,5". Decidir divergência lendo só "Manter" obriga a pessoa a lembrar qual
 * dos três números da linha é o que fica.
 */
export function rotuloDaDecisao(
  abertura: AberturaEmDivergencia,
  decisao: DecisaoDaDivergencia,
): string {
  return decisao === 'Manter'
    ? `Manter ${formatarQuantidade(abertura.quantidade)}`
    : `Atualizar para ${exibir(somaDasEntregas(saldoDe(abertura)))}`;
}

type Props = {
  readonly abertura: AberturaEmDivergencia;
  readonly decisao: DecisaoDaDivergencia;
  readonly aoDecidir: (decidida: DivergenciaDecidida) => void;
  readonly aoCancelar: () => void;
};

/**
 * Decisão da divergência de quantidade (r1-mov, passo 2.2; RF-063).
 *
 * O diálogo escreve a consequência EM NÚMERO antes do clique: com `Manter`, a
 * entrega vai ao ERP só com o que faltava, e o valor que o operador informou
 * fica guardado; com `Atualizar`, a movimentação passa a valer a soma e a
 * entrega vai inteira. A decisão não se desfaz — a segunda tentativa volta
 * `ABERTURA_SEM_DIVERGENCIA`, e a recusa aparece aqui dentro, sem fechar.
 */
export function DialogoDeDecisao({ abertura, decisao, aoDecidir, aoCancelar }: Props) {
  const [justificativa, definirJustificativa] = useState('');
  const [agindo, definirAgindo] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);

  const saldo = saldoDe(abertura);
  const iniciada = formatarQuantidade(abertura.quantidade);
  const informada = formatarQuantidade(abertura.retida.quantidade);
  const rotulo = rotuloDaDecisao(abertura, decisao);

  async function confirmar() {
    definirAgindo(true);
    definirErro(null);

    try {
      aoDecidir(await decidirDivergencia(abertura.id, { decisao, justificativa }));
    } catch (falha: unknown) {
      definirErro(mensagemDoErro(falha));
      definirAgindo(false);
    }
  }

  return (
    <DialogoConfirmacao
      titulo={
        decisao === 'Manter'
          ? `Manter a movimentação de ${abertura.produto} em ${iniciada}?`
          : `Atualizar a movimentação de ${abertura.produto}?`
      }
      rotuloConfirmar={rotulo}
      rotuloCancelar="Voltar"
      variante="primario"
      agindo={agindo}
      aoConfirmar={() => {
        void confirmar();
      }}
      aoCancelar={aoCancelar}
    >
      {decisao === 'Manter' ? (
        <p>
          A movimentação continua com {iniciada}. A entrega retida vai ao ERP com{' '}
          {exibir(quantidadeQueFaltava(saldo))}, o que faltava da quantidade iniciada; o operador
          informou {informada}, e esse valor fica guardado na trilha.
        </p>
      ) : (
        <p>
          A quantidade da movimentação passa de {iniciada} para {exibir(somaDasEntregas(saldo))}, a
          soma das entregas, e a entrega retida de {informada} vai inteira ao ERP.
        </p>
      )}
      <p>A decisão fica na auditoria com o seu login e não se desfaz.</p>
      <CampoAreaTexto
        rotulo="Justificativa (opcional)"
        valor={justificativa}
        aoMudar={definirJustificativa}
        maximoDeCaracteres={LIMITE_DO_TEXTO}
        desabilitado={agindo}
      />
      {erro !== null && <p className="dialogo-abertura__erro">{erro}</p>}
    </DialogoConfirmacao>
  );
}
